import { type ReactNode, useRef, createContext, useContext } from 'react';
import { type StoreApi, useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export const createZustandStoreProvider = <
  S extends object,
  P extends object,
  M extends object = object,
>(
  creator: (params: P) => StoreApi<S> & M,
) => {
  const Provider = createContext<(StoreApi<S> & M) | null>(null);

  return [
    function ZustandStoreProvider({
      children,
      ...params
    }: {
      children: ReactNode;
    } & P) {
      const storeRef = useRef<StoreApi<S> & M>(null);
      if (!storeRef.current) {
        //@ts-expect-error generics
        storeRef.current = creator(params);
      }

      return (
        <Provider.Provider value={storeRef.current}>
          {children}
        </Provider.Provider>
      );
    },
    //useStateStoreContext
    () => {
      const v = useContext(Provider);
      if (!v) {
        console.log(`state store context is nullish`);
      }
      return v;
    },
    // useStateStoreIs
    // vscode syntax highlight got broken
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
    <U extends unknown = S>(selector: (state: S) => U) => {
      const storeContext = useContext(Provider);

      if (!storeContext) {
        throw new Error(`useStoreProvider lacks a Provider`);
      }

      return useStore(storeContext, selector);
    },
    // useStateStoreShallow
    // vscode syntax highlight got broken
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
    <U extends unknown = S>(selector: (state: S) => U) => {
      const storeContext = useContext(Provider);

      if (!storeContext) {
        throw new Error(`useStoreProvider lacks a Provider`);
      }

      return useStore(storeContext, useShallow(selector));
    },
  ] as const;
};
